/**
 * 実績紹介シングルページ 縦カルーセルモジュール
 * 
 * @package LIVMO
 * @version 1.0.0
 */

class ArchiveSingleCarousel {
  constructor() {
    this.carousel = document.querySelector('.archive-single-hero__carousel');
    this.swiper = null;
    this.wrapper = null;
    this.scrollPosition = 0;
    this.scrollSpeed = 0.5;
    this.animationFrameId = null;
    this.isScrolling = false;
    this.originalHeight = 0;
    this.originalWidth = 0;
    this.isMobile = window.innerWidth <= 767;
    
    if (this.carousel) {
      this.init();
    }
  }
  
  /**
   * 初期化
   */
  async init() {
    if (!this.carousel) {
      return;
    }
    
    // Swiperが既に読み込まれているか確認、なければ読み込む
    if (typeof Swiper === 'undefined') {
      await this.loadSwiper();
    }
    
    this.initSwiper();
  }
  
  /**
   * Swiperライブラリの動的読み込み
   */
  async loadSwiper() {
    return new Promise((resolve, reject) => {
      if (typeof Swiper !== 'undefined') {
        resolve();
        return;
      }
      
      const swiperScript = document.createElement('script');
      swiperScript.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
      swiperScript.async = true;
      
      const swiperCSS = document.createElement('link');
      swiperCSS.rel = 'stylesheet';
      swiperCSS.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
      
      const cssPromise = new Promise((res) => {
        swiperCSS.onload = () => res();
        swiperCSS.onerror = () => res();
      });
      document.head.appendChild(swiperCSS);
      
      swiperScript.onload = () => {
        resolve();
      };
      swiperScript.onerror = () => {
        reject();
      };
      document.head.appendChild(swiperScript);
    });
  }
  
  /**
   * Swiper初期化（スマホ時は横方向、デスクトップ時は縦方向・連続スクロール）
   */
  initSwiper() {
    if (typeof Swiper === 'undefined') {
      return;
    }
    
    if (!this.carousel) {
      return;
    }
    
    
    const wrapper = this.carousel.querySelector('.swiper-wrapper');
    if (!wrapper) {
      return;
    }
    this.wrapper = wrapper;
    
    // スマホ判定を更新
    this.isMobile = window.innerWidth <= 767;
    
    const config = {
      direction: this.isMobile ? 'horizontal' : 'vertical',
      slidesPerView: this.isMobile ? 1.5 : 3,
      spaceBetween: this.isMobile ? 20 : 20,
      loop: false,
      speed: 0,
      allowTouchMove: false,
      autoHeight: false,
      on: {
        init: () => {
          setTimeout(() => {
            this.startContinuousScroll();
          }, 200);
        },
      }
    };

    this.swiper = new Swiper(this.carousel, config);
  }
  
  /**
   * 連続スクロールを開始
   */
  startContinuousScroll() {
    if (!this.wrapper) {
      return;
    }
    
    // 画像の読み込みを待つ
    const images = this.wrapper.querySelectorAll('img');
    let loadedImages = 0;
    const totalImages = images.length;
    
    if (totalImages === 0) {
      this.initScroll();
      return;
    }
    
    const checkImagesLoaded = () => {
      loadedImages++;
      if (loadedImages === totalImages) {
        this.initScroll();
      }
    };
    
    images.forEach(img => {
      if (img.complete) {
        checkImagesLoaded();
      } else {
        img.addEventListener('load', checkImagesLoaded);
        img.addEventListener('error', checkImagesLoaded);
      }
    });
  }
  
  /**
   * スクロール初期化
   */
  initScroll() {
    if (!this.wrapper) {
      return;
    }
    
    // スマホ判定を更新
    this.isMobile = window.innerWidth <= 767;
    
    // 元のスライドを取得（複製前）
    const originalSlides = Array.from(this.wrapper.querySelectorAll('.swiper-slide'));
    if (originalSlides.length === 0) {
      return;
    }
    
    if (this.isMobile) {
      // スマホ時：横方向スクロール
      // 各スライドの幅を計算（spaceBetweenを考慮）
      const slideWidths = [];
      let originalTotalWidth = 0;
      originalSlides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const width = rect.width || slide.offsetWidth;
        slideWidths.push(width);
        originalTotalWidth += width;
        // 最後のスライド以外はspaceBetweenを追加
        if (index < originalSlides.length - 1) {
          originalTotalWidth += 20;
        }
      });
      
      if (originalTotalWidth === 0) {
        return;
      }
      
      // スライドを3セット複製して、十分な長さを確保（シームレスなループのため）
      for (let i = 0; i < 3; i++) {
        originalSlides.forEach(slide => {
          const clone = slide.cloneNode(true);
          this.wrapper.appendChild(clone);
        });
      }
      
      this.originalWidth = originalTotalWidth;
      this.isScrolling = true;
      this.scrollPosition = 0;
      
      const animate = () => {
        if (!this.isScrolling || !this.wrapper) {
          return;
        }
        
        this.scrollPosition += this.scrollSpeed;
        
        // 元のスライドの幅分スクロールしたら、位置をリセット（シームレスにループ）
        if (this.scrollPosition >= this.originalWidth) {
          this.scrollPosition = this.scrollPosition % this.originalWidth;
        }
        
        this.wrapper.style.transform = `translate3d(${-this.scrollPosition}px, 0, 0)`;
        this.wrapper.style.transition = 'none';
        
        this.animationFrameId = requestAnimationFrame(animate);
      };
      
      this.animationFrameId = requestAnimationFrame(animate);
    } else {
      // デスクトップ時：縦方向スクロール（既存のロジック）
    // 各スライドの高さを計算（spaceBetweenを考慮）
    const slideHeights = [];
    let originalTotalHeight = 0;
    originalSlides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const height = rect.height || slide.offsetHeight;
      slideHeights.push(height);
      originalTotalHeight += height;
      // 最後のスライド以外はspaceBetweenを追加
      if (index < originalSlides.length - 1) {
        originalTotalHeight += 20;
      }
    });
    
    if (originalTotalHeight === 0) {
      return;
    }
    
    // スライドを3セット複製して、十分な長さを確保（シームレスなループのため）
    for (let i = 0; i < 3; i++) {
      originalSlides.forEach(slide => {
        const clone = slide.cloneNode(true);
        this.wrapper.appendChild(clone);
      });
    }
    
    this.originalHeight = originalTotalHeight;
    this.isScrolling = true;
    this.scrollPosition = 0;
    
    const animate = () => {
      if (!this.isScrolling || !this.wrapper) {
        return;
      }
      
      this.scrollPosition += this.scrollSpeed;
      
      // 元のスライドの高さ分スクロールしたら、位置をリセット（シームレスにループ）
      // リセット位置を正確に計算して、視覚的な途切れを防ぐ
      if (this.scrollPosition >= this.originalHeight) {
        // リセット時に、現在の位置から元の高さを引く（余りを保持）
        this.scrollPosition = this.scrollPosition % this.originalHeight;
      }
      
      this.wrapper.style.transform = `translate3d(0, ${-this.scrollPosition}px, 0)`;
      this.wrapper.style.transition = 'none';
      
      this.animationFrameId = requestAnimationFrame(animate);
    };
    
    this.animationFrameId = requestAnimationFrame(animate);
    }
  }
  
  /**
   * スクロールを停止
   */
  stopContinuousScroll() {
    this.isScrolling = false;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
  
  /**
   * 破棄
   */
  destroy() {
    this.stopContinuousScroll();
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    this.wrapper = null;
  }
}

// DOMContentLoadedで初期化
function bootArchiveSingleCarousel() {
  if (document.querySelector('.archive-single-hero__carousel')) {
    if (!window.__archiveSingleCarouselInstance) {
      window.__archiveSingleCarouselInstance = new ArchiveSingleCarousel();
    }
  }
}

document.addEventListener('DOMContentLoaded', bootArchiveSingleCarousel);

export default ArchiveSingleCarousel;

